import * as React from 'react';
import { cn } from '@relay-towing/tokens';
import {
  priorityTagRecipe,
  recordTagDotRecipe,
  weightClassBarRecipe,
  weightClassRecipe,
} from '../recipes/list.recipe';
import { Inline, Stack } from './Layout';
import { ListRow, type ListRowProps } from './List';
import { vehicleColorSwatch } from './vehicle-color';
import { Text } from './Text';

type Density = 'comfortable' | 'compact' | 'card';

export interface RecordRowProps
  extends Omit<ListRowProps, 'title' | 'children' | 'as'> {
  /** What the record is, in the words a person looks for: an address, a car. */
  title: React.ReactNode;
  /** The line under it: numbers, a plate, a customer. */
  meta?: React.ReactNode;
  /** A third line, left out in `compact`: a lot location, a date. */
  detail?: React.ReactNode;
  /** Above the title, on a line of its own: a priority tag. */
  badge?: React.ReactNode;
  /** Top right: the status. */
  status?: React.ReactNode;
  /** Bottom right, level with the last line: a total, a time, days at the lot. */
  figure?: React.ReactNode;
  /**
   * `compact` drops the third line and tightens the row, for a long list.
   * `card` is the row of a grouped list column, under a ListBand: a
   * RecordRowTag in `badge`, what it is in `title`, one more fact in `meta`.
   */
  density?: Density;
}

/**
 * One record in a phone list — a call, an impound, an invoice. What it is and
 * the line under it on the left; its status top right and a figure bottom
 * right, level with the last line.
 *
 *   <Stack divided gap={0}>
 *     <RecordRow title="2019 Ford F-150" meta="8ABC123 · IMP-0042"
 *       detail="Front lot, row C" status={<Badge tone="warning">At lot</Badge>}
 *       figure="12 days" onClick={open} />
 *   </Stack>
 *
 * Drawn to the Figma's calls list: no card, a rule between rows with 16px
 * either side, the title at the large body size and the line under it in
 * the same size, muted. A button when it has an onClick. A record that
 * has a card in ResourceCards.tsx uses that; reach for this only for a list
 * that has none.
 *
 * `density="card"` is the row of a grouped list column, as ResourceCards.tsx
 * draws each kind of record (Figma 1027:25114).
 */
export const RecordRow = React.forwardRef<HTMLElement, RecordRowProps>(
  function RecordRow(
    {
      title,
      meta,
      detail,
      badge,
      status,
      figure,
      density = 'comfortable',
      onClick,
      ...props
    },
    ref
  ) {
    const compact = density === 'compact';
    const card = density === 'card';
    return (
      <ListRow
        ref={ref}
        as={onClick ? 'button' : 'div'}
        interactive={Boolean(onClick)}
        inset={compact ? 'sm' : card ? 'md' : 'ruled'}
        shape={card ? 'card' : undefined}
        aria-current={props.selected ? 'true' : undefined}
        onClick={onClick}
        {...props}
      >
        <Stack gap={2} grow align="start">
          {badge}
          {/* Full width, or `align="start"` sizes this to its words and a long
              line runs under the figure instead of truncating. */}
          <Stack gap={1} className="w-full">
            <Text as="span" variant="body-lg-strong" truncate>
              {title}
            </Text>
            {meta ? (
              <Text as="span" variant="body-lg" tone="muted" truncate>
                {meta}
              </Text>
            ) : null}
            {detail && !compact ? (
              <Text as="span" variant="body" tone="muted" truncate>
                {detail}
              </Text>
            ) : null}
          </Stack>
        </Stack>
        {status || figure ? (
          <Stack
            gap={2}
            align="end"
            justify="between"
            // Capped, or a long account name takes the row from the title.
            className="max-w-[45%] shrink-0 self-stretch"
          >
            {status}
            {figure ? (
              <Text
                as="span"
                variant="body"
                tone="muted"
                numeric
                truncate
                className="max-w-full"
              >
                {figure}
              </Text>
            ) : null}
          </Stack>
        ) : null}
      </ListRow>
    );
  }
);

export type WeightClass = 'light' | 'medium' | 'heavy';

const WEIGHT_STEPS: Record<WeightClass, number> = {
  light: 1,
  medium: 2,
  heavy: 3,
};

const WEIGHT_LABEL: Record<WeightClass, string> = {
  light: 'Light duty',
  medium: 'Medium duty',
  heavy: 'Heavy duty',
};

/** The word beside the bars in a row, as the Figma writes it. */
export const WEIGHT_WORD: Record<WeightClass, string> = {
  light: 'Light',
  medium: 'Medium',
  heavy: 'Heavy',
};

export interface WeightClassBarsProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  weightClass: WeightClass;
  /** `dashboard` sits level with a line of a dashboard card. */
  size?: 'phone' | 'dashboard';
}

/**
 * Light, medium or heavy duty as three rising bars, as many filled as the
 * class is heavy. Named in words for a screen reader.
 */
export const WeightClassBars = React.forwardRef<
  HTMLSpanElement,
  WeightClassBarsProps
>(function WeightClassBars(
  { weightClass, size = 'phone', className, ...props },
  ref
) {
  const filled = WEIGHT_STEPS[weightClass];
  return (
    <span
      ref={ref}
      role="img"
      aria-label={WEIGHT_LABEL[weightClass]}
      className={cn(weightClassRecipe({ size }), className)}
      {...props}
    >
      {([1, 2, 3] as const).map((step) => (
        <span
          key={step}
          className={weightClassBarRecipe({ step, filled: step <= filled })}
        />
      ))}
    </span>
  );
});

export type CallPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';

const PRIORITY: Record<
  CallPriority,
  { label: string; tone: 'neutral' | 'warning' | 'destructive' }
> = {
  LOW: { label: 'Low', tone: 'neutral' },
  NORMAL: { label: 'Normal', tone: 'neutral' },
  HIGH: { label: 'High', tone: 'warning' },
  URGENT: { label: 'Urgent', tone: 'destructive' },
};

export interface PriorityTagProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'children'> {
  priority: CallPriority;
  /** `dashboard` is a Badge's size, for a dashboard card. */
  size?: 'phone' | 'dashboard';
}

/**
 * A call's priority as a small square tag: quiet grey for normal and low,
 * amber for high, red for urgent. The calls list draws it above each
 * address; a call's card draws it beside its number.
 *
 *   <PriorityTag priority="HIGH" />
 */
export const PriorityTag = React.forwardRef<HTMLSpanElement, PriorityTagProps>(
  function PriorityTag({ priority, size = 'phone', className, ...props }, ref) {
    return (
      <span
        ref={ref}
        className={cn(
          priorityTagRecipe({ tone: PRIORITY[priority].tone, size }),
          className
        )}
        {...props}
      >
        {PRIORITY[priority].label}
      </span>
    );
  }
);

export interface RecordRowTagProps {
  /** A vehicle's colour as it was typed. A word the dot does not know draws none. */
  color?: string | null;
  /** The number: "#3254". */
  children?: React.ReactNode;
}

/**
 * The line above a card's title: a vehicle's colour, as a dot and a word,
 * then the record's number.
 *
 *   <RecordRowTag color="Red">#3254</RecordRowTag>
 */
export function RecordRowTag({ color, children }: RecordRowTagProps) {
  const swatch = vehicleColorSwatch(color);
  return (
    <Inline gap={1} align="center">
      {swatch ? (
        <span
          aria-hidden
          className={recordTagDotRecipe()}
          style={{ backgroundColor: swatch }}
        />
      ) : null}
      <Text as="span" variant="body" tone="muted" truncate>
        {[color, children].filter(Boolean).length > 1 ? (
          <>
            {color} • {children}
          </>
        ) : (
          (color ?? children)
        )}
      </Text>
    </Inline>
  );
}
